import { TermResponseSubmissionDto } from '@act/shared/data-transfer-objects';
import { Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { Box } from '@act/storybook';
import { userAppSelectors } from 'redux/slices/usersSlice.slice';

/** One row in the consent history list. */
export interface ConsentHistoryEntryProps {
  consented?: boolean;
  termResponseSubmission?: TermResponseSubmissionDto;
}

/**
 * One consent history row: date, optional language, and who accepted or rejected.
 *
 * @param consented - Whether this signature accepted the term
 * @param termResponseSubmission - Submission metadata for the signature
 */
const ConsentHistoryEntry = ({
  consented,
  termResponseSubmission,
}: ConsentHistoryEntryProps) => {
  const users = userAppSelectors.useSelectUsersMap();

  if (!termResponseSubmission) {
    return (
      <Typography variant="bodyMedium">
        No record found for response submission
      </Typography>
    );
  }

  const termAcceptedByMember = !!termResponseSubmission.consentLinkId;
  const user = users[termResponseSubmission.internalSubmitterUserId];

  let message: string;
  if (termResponseSubmission.ipAddress === 'SMS') {
    message = 'Rejected by member via SMS response';
  } else {
    const userName = user ? `${user?.name}` : 'Unknown user';
    const status = consented ? 'Accepted' : 'Rejected';

    const acceptedBy = termAcceptedByMember
      ? 'by member in web form'
      : `by ${userName} in app`;
    message = `${status} ${acceptedBy}`;
  }

  return (
    <Box display="flex" flexDirection="row" gap="16px">
      <Typography
        variant="bodyMedium"
        className="consent-history-entry-date"
      >
        {DateTime.fromMillis(termResponseSubmission.createdAt).toFormat(
          'MM/dd/yyyy'
        )}
      </Typography>
      <Typography variant="bodyMedium">
        {termAcceptedByMember && termResponseSubmission.languageCode && (
          <Typography
            component="span"
            className="consent-history-entry-language-code"
          >
            {termResponseSubmission.languageCode}
          </Typography>
        )}
        {message}
      </Typography>
    </Box>
  );
};

export default ConsentHistoryEntry;
